import { Box, Flex, SimpleGrid, Table, TableContainer, Tbody, Td, Text, Tfoot, Th, Thead, Tr } from '@chakra-ui/react'
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ActionButton } from '../../../../components/ActionButton'
import { ConfirmDialog } from '../../../../components/ConfirmDialog'
import { EmptyState } from '../../../../components/EmptyState'
import { FilterBar, FilterSearch, FilterSelect } from '../../../../components/FilterBar'
import { LoadingBlock, TableSkeletonRows } from '../../../../components/Loading'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../../components/paginator'
import { SectionCard, StatCard } from '../../../../components/SectionCard'
import { StatusBadge, statusOptions } from '../../../../components/StatusBadge'
import { TableEmptyRow, TableErrorRow } from '../../../../components/TableEmptyRow'
import { formatCurrency, formatCurrencyOrFree, formatDateTime, useAppToast } from '../../../../helpers'
import { EventEstablishmentService } from '../../../../services/Event/EventEstablishmentService'
import {
  EstablishmentAttendeesResponse,
  EstablishmentOrder,
  EstablishmentOrderStatus,
  EstablishmentSalesSummary
} from '../../../../services/Event/types'
import { ESTABLISHMENT_ORDER_STATUS } from './establishmentStatus'

interface IProps {
  eventId: number
  /** Cancelar uma venda exige `edit_event`: quem edita o evento cuida dos parceiros. */
  canCancel: boolean
}

const INITIAL_FILTERS = { search: '', establishmentId: '', status: '' }
const ORDER_COLUMNS = 8
const ATTENDEE_COLUMNS = 4

/**
 * Corpo da tela "Vendas dos Parceiros" (ABRA-317): o relatório das vendas dos estabelecimentos (por
 * estabelecimento e produto) e a lista detalhada, onde uma venda em aberto pode ser cancelada. A
 * exportação fica no topo da tela.
 *
 * Tudo conta as vendas não canceladas — em aberto ou já pagas no checkout —, e cada linha da lista
 * diz em que pé está a venda. Quando há oficina (ABRA-399), uma seção lista quem ocupa cada vaga, e
 * cada oficina imprime a sua lista de presença (PDF da API) — ou todas de uma vez, uma por página.
 */
export function EstablishmentSalesSection({ eventId, canCancel }: IProps) {
  const service = useMemo(() => new EventEstablishmentService(), [])
  const toast = useAppToast()

  const [summary, setSummary] = useState<EstablishmentSalesSummary | null>(null)
  const [summaryLoading, setSummaryLoading] = useState(true)
  const [summaryError, setSummaryError] = useState(false)

  const [orders, setOrders] = useState<EstablishmentOrder[]>([])
  const [ordersTotal, setOrdersTotal] = useState(0)
  const [ordersLoading, setOrdersLoading] = useState(true)
  const [ordersError, setOrdersError] = useState(false)
  const [filters, setFilters] = useState(INITIAL_FILTERS)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  const [attendees, setAttendees] = useState<EstablishmentAttendeesResponse | null>(null)
  const [attendeesLoading, setAttendeesLoading] = useState(true)
  const [attendeesError, setAttendeesError] = useState(false)
  // Qual lista de presença está sendo gerada: `'all'` é o "Imprimir todas", um número é a oficina.
  // Um estado para a seção, e não um `useDownload` por botão, porque os botões saem de uma lista.
  const [printing, setPrinting] = useState<'all' | number | null>(null)

  const [cancelTarget, setCancelTarget] = useState<EstablishmentOrder | null>(null)
  // Só a resposta da busca mais recente vale: trocar de filtro depressa não pode deixar na tela
  // as vendas do filtro anterior, nem desligar o esqueleto da busca que ainda está no ar.
  const ordersRequestRef = useRef(0)

  // Recarregar (depois de cancelar uma venda) mantém o relatório na tela até a resposta chegar: só a
  // primeira carga mostra o carregando no lugar dele.
  const loadSummary = useCallback(() => {
    setSummaryLoading(true)
    setSummaryError(false)
    service
      .getSummary(eventId)
      .then(setSummary)
      .catch((error) => {
        setSummaryError(true)
        toast.error('Erro ao carregar o relatório dos parceiros', error)
      })
      .finally(() => setSummaryLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, service])

  const loadOrders = useCallback(() => {
    const requestId = ++ordersRequestRef.current
    setOrdersLoading(true)
    setOrdersError(false)
    service
      .getOrders(eventId, {
        search: filters.search || undefined,
        establishment_id: filters.establishmentId ? Number(filters.establishmentId) : undefined,
        status: (filters.status || undefined) as EstablishmentOrderStatus | undefined,
        page,
        page_size: pageSize
      })
      .then((response) => {
        if (requestId !== ordersRequestRef.current) return
        setOrders(response.data)
        setOrdersTotal(response.total)
      })
      .catch((error) => {
        if (requestId !== ordersRequestRef.current) return
        setOrdersError(true)
        toast.error('Erro ao carregar as vendas dos parceiros', error)
      })
      .finally(() => {
        if (requestId === ordersRequestRef.current) setOrdersLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, service, filters, page, pageSize])

  // Como o relatório, recarregar mantém a lista na tela até a resposta chegar. O erro só sai no
  // sucesso: limpá-lo ao começar esconderia a seção (sem lista, ela só existe por causa do erro)
  // justamente quando o "Tentar novamente" devia mostrar o esqueleto.
  const loadAttendees = useCallback(() => {
    setAttendeesLoading(true)
    service
      .getAttendees(eventId)
      .then((response) => {
        setAttendees(response)
        setAttendeesError(false)
      })
      .catch((error) => {
        setAttendeesError(true)
        toast.error('Erro ao carregar os inscritos nas oficinas', error)
      })
      .finally(() => setAttendeesLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, service])

  useEffect(() => {
    loadSummary()
  }, [loadSummary])

  useEffect(() => {
    loadAttendees()
  }, [loadAttendees])

  useEffect(() => {
    loadOrders()
  }, [loadOrders])

  const updateFilters = (patch: Partial<typeof INITIAL_FILTERS>) => {
    setFilters({ ...filters, ...patch })
    setPage(1)
  }

  const cancelOrder = async () => {
    if (!cancelTarget) return
    try {
      await service.cancelOrder(eventId, cancelTarget.id)
      toast.success(`Venda #${cancelTarget.number} cancelada`)
      setCancelTarget(null)
      loadSummary()
      loadOrders()
      loadAttendees()
    } catch (error) {
      toast.error('Erro ao cancelar a venda', error)
      // A venda mudou por fora (paga no checkout, cancelada por outra pessoa): a linha não pode
      // continuar mostrando "Em aberto" com o botão de cancelar.
      setCancelTarget(null)
      loadSummary()
      loadOrders()
      loadAttendees()
    }
  }

  const printAttendanceList = (productId?: number) => {
    setPrinting(productId ?? 'all')
    service
      .printAttendanceList(eventId, productId)
      .catch((error) => toast.error('Erro ao gerar a lista de presença', error))
      .finally(() => setPrinting(null))
  }

  const hasAnyAttendee = attendees?.establishments.some((e) => e.workshops.some((w) => w.attendees.length > 0)) ?? false
  const establishmentOptions = (summary?.establishments ?? []).map((e) => ({ value: e.id, label: e.name }))
  const isDirty = filters.search !== '' || filters.establishmentId !== '' || filters.status !== ''

  return (
    <Box mb={6}>
      {summaryLoading && !summary ? (
        <LoadingBlock entity="vendas dos parceiros" />
      ) : summaryError || !summary ? (
        <EmptyState
          icon="lni lni-shopping-basket"
          tone="error"
          title="Não foi possível carregar o relatório dos parceiros"
          action={<ActionButton action="refresh" label="Tentar novamente" showLabel mr={0} onClick={loadSummary} />}
        />
      ) : (
        <>
          <SimpleGrid columns={{ base: 2, xl: 4 }} spacing={4} mb={6}>
            <StatCard
              label="Valor vendido"
              value={formatCurrency(summary.totals.total)}
              helpText={`${summary.totals.quantity} ${summary.totals.quantity === 1 ? 'item' : 'itens'}`}
              color="magenta.500"
              icon="lni lni-shopping-basket"
            />
            <StatCard
              label="Lucro da ABRA"
              value={formatCurrency(summary.totals.abra_fee)}
              color="abraPurple.500"
              icon="lni lni-coin"
            />
            <StatCard
              label="A repassar"
              value={formatCurrency(summary.totals.partner_amount)}
              helpText="aos estabelecimentos"
              color="green.500"
              icon="lni lni-handshake"
            />
            <StatCard
              label="Vendas em aberto"
              value={formatCurrency(summary.orders.open.total)}
              helpText={`${summary.orders.open.count} a cobrar no checkout`}
              color="orange.500"
              icon="lni lni-timer"
            />
          </SimpleGrid>

          <SectionCard title="Vendas por estabelecimento" variant="flush" titleAs="h3" mb={6}>
            <TableContainer>
              <Table>
                <Thead>
                  <Tr>
                    <Th>Estabelecimento / produto</Th>
                    <Th isNumeric>Quantidade</Th>
                    <Th isNumeric>Valor vendido</Th>
                    <Th isNumeric>Lucro ABRA</Th>
                    <Th isNumeric>A repassar</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {summary.establishments.length === 0 ? (
                    <TableEmptyRow colSpan={5} entity="estabelecimento" />
                  ) : (
                    summary.establishments.map((establishment) => (
                      <Fragment key={establishment.id}>
                        <Tr bg="gray.50">
                          <Td fontWeight="600">{establishment.name}</Td>
                          <Td isNumeric fontWeight="600">
                            {establishment.totals.quantity}
                          </Td>
                          <Td isNumeric fontWeight="600">
                            {formatCurrency(establishment.totals.total)}
                          </Td>
                          <Td isNumeric fontWeight="600">
                            {formatCurrency(establishment.totals.abra_fee)}
                          </Td>
                          <Td isNumeric fontWeight="600">
                            {formatCurrency(establishment.totals.partner_amount)}
                          </Td>
                        </Tr>
                        {establishment.products.map((product) => (
                          <Tr key={product.id}>
                            <Td>
                              {/* Recuo do produto sob o estabelecimento — no conteúdo, não na célula. */}
                              <Text as="span" pl={4}>
                                {product.name}
                              </Text>
                            </Td>
                            <Td isNumeric>{product.quantity}</Td>
                            <Td isNumeric>{formatCurrency(product.total)}</Td>
                            <Td isNumeric>{formatCurrency(product.abra_fee)}</Td>
                            <Td isNumeric>{formatCurrency(product.partner_amount)}</Td>
                          </Tr>
                        ))}
                      </Fragment>
                    ))
                  )}
                </Tbody>
                {summary.establishments.length > 0 && (
                  <Tfoot>
                    <Tr>
                      <Th>Total</Th>
                      <Th isNumeric>{summary.totals.quantity}</Th>
                      <Th isNumeric>{formatCurrency(summary.totals.total)}</Th>
                      <Th isNumeric>{formatCurrency(summary.totals.abra_fee)}</Th>
                      <Th isNumeric>{formatCurrency(summary.totals.partner_amount)}</Th>
                    </Tr>
                  </Tfoot>
                )}
              </Table>
            </TableContainer>
          </SectionCard>
        </>
      )}

      {/* Só existe com oficina: sem nenhuma, a seção não aparece (nem enquanto carrega). Com a lista na
          mão, ela decide; o erro sozinho só mostra a seção quando a primeira carga falhou, porque aí
          não se sabe se há oficina. */}
      {(attendees ? attendees.establishments.length > 0 : attendeesError) && (
        <SectionCard
          title="Inscritos nas oficinas"
          variant="flush"
          titleAs="h3"
          mb={6}
          actions={
            <ActionButton
              action="print"
              label="Imprimir todas"
              showLabel
              mr={0}
              isLoading={printing === 'all'}
              isDisabled={!hasAnyAttendee || (printing !== null && printing !== 'all')}
              onClick={() => printAttendanceList()}
            />
          }
        >
          <TableContainer>
            <Table>
              <Thead>
                <Tr>
                  <Th>Oficina / inscrito</Th>
                  <Th>Venda</Th>
                  <Th>Comprador (cartão)</Th>
                  <Th>Status</Th>
                </Tr>
              </Thead>
              <Tbody>
                {attendeesLoading && !attendees ? (
                  <TableSkeletonRows cols={ATTENDEE_COLUMNS} />
                ) : attendeesError || !attendees ? (
                  <TableErrorRow colSpan={ATTENDEE_COLUMNS} entity="inscritos nas oficinas" onRetry={loadAttendees} />
                ) : (
                  attendees.establishments.flatMap((establishment) =>
                    establishment.workshops.map((workshop) => (
                      <Fragment key={workshop.product_id}>
                        <Tr bg="gray.50">
                          <Td fontWeight="600" colSpan={ATTENDEE_COLUMNS}>
                            <Flex align="center" justify="space-between" gap={2}>
                              <Box>
                                {establishment.name} — {workshop.name}
                                <Text as="span" variant="caption" ml={2}>
                                  {workshop.attendees.length === 1
                                    ? '1 inscrito'
                                    : `${workshop.attendees.length} inscritos`}
                                </Text>
                              </Box>
                              <ActionButton
                                action="print"
                                label="Imprimir lista de presença"
                                size="xs"
                                mr={0}
                                isLoading={printing === workshop.product_id}
                                isDisabled={
                                  workshop.attendees.length === 0 ||
                                  (printing !== null && printing !== workshop.product_id)
                                }
                                onClick={() => printAttendanceList(workshop.product_id)}
                              />
                            </Flex>
                          </Td>
                        </Tr>
                        {workshop.attendees.length === 0 ? (
                          <TableEmptyRow colSpan={ATTENDEE_COLUMNS} message="Nenhuma vaga vendida" />
                        ) : (
                          workshop.attendees.map((attendee, i) => (
                            <Tr key={`${attendee.order_id}-${i}`}>
                              <Td>
                                {/* Recuo do inscrito sob a oficina — no conteúdo, não na célula. */}
                                <Text as="span" pl={4}>
                                  {attendee.name}
                                </Text>
                              </Td>
                              <Td>#{attendee.order_number}</Td>
                              <Td>{attendee.buyer_name}</Td>
                              <Td>
                                <StatusBadge status={attendee.status} statuses={ESTABLISHMENT_ORDER_STATUS} />
                              </Td>
                            </Tr>
                          ))
                        )}
                      </Fragment>
                    ))
                  )
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </SectionCard>
      )}

      <SectionCard title="Vendas" variant="flush" titleAs="h3">
        <Box px={4} pt={4}>
          <FilterBar onClear={() => updateFilters(INITIAL_FILTERS)} isDirty={isDirty}>
            <FilterSearch
              value={filters.search}
              onChange={(search) => updateFilters({ search })}
              placeholder="comprador / nº da venda"
            />
            <FilterSelect
              label="Estabelecimento"
              value={filters.establishmentId}
              onChange={(establishmentId) => updateFilters({ establishmentId })}
              options={establishmentOptions}
            />
            <FilterSelect
              label="Status"
              value={filters.status}
              onChange={(status) => updateFilters({ status })}
              options={statusOptions(ESTABLISHMENT_ORDER_STATUS)}
            />
          </FilterBar>
        </Box>

        <TableContainer>
          <Table>
            <Thead>
              <Tr>
                <Th>Nº</Th>
                <Th>Data/hora</Th>
                <Th>Estabelecimento</Th>
                <Th>Comprador</Th>
                <Th>Produtos</Th>
                <Th isNumeric>Total</Th>
                <Th>Status</Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {ordersLoading ? (
                <TableSkeletonRows cols={ORDER_COLUMNS} />
              ) : ordersError ? (
                <TableErrorRow colSpan={ORDER_COLUMNS} entity="vendas" feminine onRetry={loadOrders} />
              ) : orders.length === 0 ? (
                <TableEmptyRow colSpan={ORDER_COLUMNS} entity="venda" feminine />
              ) : (
                orders.map((order) => (
                  <Tr key={order.id}>
                    <Td fontWeight="600">#{order.number}</Td>
                    <Td>{formatDateTime(order.created_at)}</Td>
                    <Td>
                      {order.establishment.name}
                      {order.origin === 'checkout' && <Text variant="caption">Lançada no checkout</Text>}
                    </Td>
                    <Td>{order.buyer_name}</Td>
                    <Td whiteSpace="normal" minW="220px">
                      {order.items
                        .filter((item) => item.quantity > 0)
                        .map((item, i) => (
                          <Text key={i} fontSize="sm">
                            {item.quantity}× {item.name}
                          </Text>
                        ))}
                    </Td>
                    <Td isNumeric>{formatCurrencyOrFree(order.total)}</Td>
                    <Td>
                      <StatusBadge status={order.status} statuses={ESTABLISHMENT_ORDER_STATUS} />
                    </Td>
                    <Td textAlign="right">
                      {/* `can_cancel` também vale para a venda grátis que o checkout liquidou sem pagamento
                          (ABRA-399); uma API antiga não manda o campo, e aí só a venda em aberto cancela. */}
                      {canCancel && (order.can_cancel ?? order.status === 'open') && (
                        <ActionButton
                          action="cancel"
                          label="Cancelar venda"
                          mr={0}
                          onClick={() => setCancelTarget(order)}
                        />
                      )}
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </TableContainer>

        <Box px={4} py={3}>
          <Paginator
            page={page}
            totalPages={Math.max(1, Math.ceil(ordersTotal / pageSize))}
            changePage={setPage}
            pageSize={pageSize}
            onPageSizeChange={(size) => {
              setPageSize(size)
              setPage(1)
            }}
          />
        </Box>
      </SectionCard>

      <ConfirmDialog
        isOpen={cancelTarget !== null}
        onClose={() => setCancelTarget(null)}
        onConfirm={cancelOrder}
        action="cancel"
        title="Cancelar venda"
        description={`A venda #${cancelTarget?.number ?? ''} de ${cancelTarget?.buyer_name ?? ''} (${formatCurrencyOrFree(
          cancelTarget?.total ?? 0
        )}) sai da comanda e os itens voltam ao estoque${
          cancelTarget?.status === 'paid' ? ' e ao limite por pessoa' : ''
        }.`}
        confirmLabel="Cancelar venda"
        cancelLabel="Voltar"
        irreversible
      />
    </Box>
  )
}
